import { uniq } from 'lodash';
import { MDSDropdown } from '@marqvision/mds-v2';
import { useQueryParams } from '@marqvision/shared';
import { useCustomTagsFilterQuery as useDomainNamesCustomTagsFilterQuery } from '~/apis/domainNames';
import { useCustomTagsFilterQuery as useWebsiteContentCustomTagsFilterQuery } from '~/apis/websiteContent';
import { ChannelGroup } from '~/components/Filters/DynamicFilter/@types';
import { useLogger } from '~/hooks';
import { useTab as useDomainNamesTab } from '~/pages/DomainNames/@hooks/useTab';
import { FilterQueryParams as DomainNamesFilterQueryParams } from '~/pages/DomainNames/@types';
import { useRequest as useDomainNamesRequest } from '~/pages/DomainNames/List/@hooks/useRequest';
import { useTab as useWebsiteContentTab } from '~/pages/WebsiteContent/_impersonation/@hooks/useTab';
import { FilterQueryParams as WebsiteContentFilterQueryParams } from '~/pages/WebsiteContent/_impersonation/@types';
import { useRequest as useWebsiteContentRequest } from '~/pages/WebsiteContent/_impersonation/List/@hooks/useRequest';
import { parseCustomTagFilterDropdown, parseValueToValueArray } from '~/utils';

interface Props {
  channel?: ChannelGroup;
}

export const GenericCustomTagFilter = (props: Props) => {
  const { channel = 'domain names' } = props;

  const { amplitudeLogging } = useLogger();

  // Channel별 hooks 선택
  const isDomainNames = channel === 'domain names';
  const { currentTabGroup: domainNamesTabGroup } = useDomainNamesTab();
  const { currentTabGroup: websiteContentTabGroup } = useWebsiteContentTab();
  const { params: domainNamesParams, setParams: setDomainNamesParams } = useQueryParams<DomainNamesFilterQueryParams>();
  const { params: websiteContentParams, setParams: setWebsiteContentParams } =
    useQueryParams<WebsiteContentFilterQueryParams>();
  const { request: domainNamesRequest } = useDomainNamesRequest();
  const { request: websiteContentRequest } = useWebsiteContentRequest();

  // Channel별 파라미터와 request 선택
  const currentTabGroup = isDomainNames ? domainNamesTabGroup : websiteContentTabGroup;
  const params = isDomainNames ? domainNamesParams : websiteContentParams;
  const setParams = isDomainNames ? setDomainNamesParams : setWebsiteContentParams;

  // Channel별 API hook 사용
  const domainNamesQuery = useDomainNamesCustomTagsFilterQuery(domainNamesRequest, {
    select: parseCustomTagFilterDropdown,
    enabled: isDomainNames,
  });
  const websiteContentQuery = useWebsiteContentCustomTagsFilterQuery(websiteContentRequest, {
    select: parseCustomTagFilterDropdown,
    enabled: !isDomainNames,
  });

  const data = isDomainNames ? domainNamesQuery.data : websiteContentQuery.data;
  const isFetching = isDomainNames ? domainNamesQuery.isFetching : websiteContentQuery.isFetching;
  const list = data?.list || [];

  const { custom_tag_id_filter } = params;
  const selectedCustomTagIds = custom_tag_id_filter ? parseValueToValueArray(custom_tag_id_filter) : [];

  const handleChange = (value: number[]) => {
    const _value = uniq(value); // MDSDropdown v2 '1-depth-single' 사용시 value 중복 이슈 해결되면 uniq함수 제거

    setParams({ custom_tag_id_filter: _value });

    if (_value.length > 0) {
      amplitudeLogging('applied filter', {
        Type: 'tags',
        Channel: channel,
        Page: currentTabGroup.replace(/-/g, ' '),
      });
    }
  };

  return (
    <MDSDropdown
      label="Tags"
      value={selectedCustomTagIds}
      list={list}
      isLoading={isFetching}
      onChange={handleChange}
      modules={['search', '1-depth-single']}
    />
  );
};
